import {
  ContactMail as ContactMailIcon,
  ContactPhone as ContactPhoneIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { Autocomplete, Box, MenuItem, TextField, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import {
  CareTeamMemberGridRowData,
  CareTeamMemberResourceTypes,
  CareTeamPermissions,
  Option,
} from 'src/@types/crs/case';
import useObjectState from 'src/hooks/useObjectState';
import { momentDateComparator } from 'src/utils/dates';
import ActionButton from 'src/components/ActionButton';
import PeriodFilter from 'src/components/PeriodFilter';
import CareTeamMemberModal from './CareTeamMemberModal';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedCareTeam } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import CareTeamMemberEditModal from './CareTeamMemberEditModal';
import { Coding, Reference } from 'src/careflow/ph-core/fhir-client/resources';
import usePermissionsContext from 'src/careflow/ph-core/admin/permissions/hook/usePermissionsContext';
import CellRow from 'src/careflow/common/CellRow';
import CustomLink from 'src/careflow/common/CustomLink';
import PractitionerDrawer from 'src/careflow/care-coordination/practitioner/provider/PractitionerDrawer';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import DataGridWithFilters, {
  GridFilterDrawerProps,
} from 'src/careflow/ph-core/grid/react/DataGridWithFilters/DataGridWithFilters';
import useGridFilterConsolidation from 'src/careflow/ph-core/grid/hook/useGridFilterConsolidation';
import IcomxInlineTelecomAction from 'src/sections/crs/patient/components/icomx/IcomxInlineTelecomAction';
import { getIcomxCandidateResourceDescriptor } from 'src/sections/crs/patient/components/icomx/icomxContactCandidateModel';
import {
  prefetchIcomxReferences,
  useReferenceIcomxCandidates,
} from 'src/sections/crs/patient/components/icomx/useIcomxContactCandidates';

export type CareTeamMemberOption = Option<CareTeamMemberResourceTypes>;

interface CareTeamMembersGridProps extends GridFilterDrawerProps<CareTeamMemberFilters> {
  patient: WrappedPatient;
  onMemberUpdateSuccess: Function;
  careTeamMembers: CareTeamMemberGridRowData[];
  careTeam: WrappedCareTeam;
  showPagination?: boolean;
  isNestedGrid?: boolean;
  showFilters?: boolean;
  associatedOrgs: Reference[];
  roles: Coding[];
}

export interface CareTeamMemberFilters {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  memberTypes: CareTeamMemberOption[];
  associatedOrg: Reference[];
  role: Coding[];
}

interface CareTeamMembersGridState {
  isModalOpen: boolean;
  isEditModalOpen: boolean;
  memberToEdit: CareTeamMemberGridRowData | null;
  associatedOrganizationIds: string[];
  isPractitionerDrawerOpen: boolean;
}

export const initialCareTeamMemberFilters: CareTeamMemberFilters = {
  startDate: null,
  endDate: null,
  memberTypes: [],
  associatedOrg: [],
  role: [],
};

const CareTeamMembersGrid = ({
  careTeamMembers,
  filterValues,
  onSearchTextFieldChange,
  searchTextFieldValue,
  onFilterDrawerOpen,
  onFilterDrawerClose,
  isFilterDrawerOpen,
  onApplyFilters,
  patient,
  showPagination = false,
  isNestedGrid = true,
  showFilters = true,
  careTeam,
  onMemberUpdateSuccess,
  associatedOrgs,
  roles,
}: CareTeamMembersGridProps) => {
  const { i18n } = useLocales();
  const { filters, updateFilters, onClearAllFilters } =
    useGridFilterConsolidation<CareTeamMemberFilters>(filterValues, initialCareTeamMemberFilters);

  const [{ isEditModalOpen, isModalOpen, memberToEdit, isPractitionerDrawerOpen }, updateState] =
    useObjectState<CareTeamMembersGridState>({
      memberToEdit: null,
      isModalOpen: false,
      isEditModalOpen: false,
      associatedOrganizationIds: [],
      isPractitionerDrawerOpen: false,
    });

  const { isAllowedToEdit } = usePermissionsContext<CareTeamPermissions['members']>() ?? {};
  const careTeamMemberReferences = useMemo(
    () =>
      Array.from(
        new Set(
          careTeamMembers
            .map((member) => member.member?.member?.reference)
            .filter((reference): reference is string => !!reference)
        )
      ),
    [careTeamMembers]
  );

  useEffect(() => {
    void prefetchIcomxReferences(careTeamMemberReferences);
  }, [careTeamMemberReferences]);

  const CareTeamMemberCell = ({ member }: { member: CareTeamMemberGridRowData }) => {
    const { name, memberType } = member;
    const { candidates } = useReferenceIcomxCandidates(
      member.member?.member?.reference,
      'Care Team Member'
    );
    const phoneCandidate =
      candidates.find((candidate) => {
        const resource = getIcomxCandidateResourceDescriptor(candidate);
        return !!candidate.phone && !!resource;
      }) ?? null;
    const emailCandidate =
      candidates.find((candidate) => {
        const resource = getIcomxCandidateResourceDescriptor(candidate);
        return !!candidate.email && !!resource;
      }) ?? null;
    const phoneResource = getIcomxCandidateResourceDescriptor(phoneCandidate);
    const emailResource = getIcomxCandidateResourceDescriptor(emailCandidate);

    return (
      <Box sx={{ py: 0.5 }}>
        {memberType === 'Practitioner' ? (
          <CustomLink
            to="#"
            onClick={() => {
              if (memberType === 'Practitioner') {
                updateState({ isPractitionerDrawerOpen: true, memberToEdit: member });
              }
            }}
          >
            {name}
          </CustomLink>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {name}
          </Typography>
        )}
        <CellRow
          tooltipTitle={i18n('patients.phone', 'crs')}
          shouldTruncateText={false}
          title={phoneCandidate?.phone}
          Icon={<ContactPhoneIcon fontSize="small" />}
          endAdornment={
            <IcomxInlineTelecomAction
              candidates={candidates}
              resourceType={phoneResource?.resourceType}
              resourceId={phoneResource?.resourceId}
              system="phone"
              tooltip="Open care team member phone in iComx"
              value={phoneCandidate?.phone}
            />
          }
        />
        <CellRow
          tooltipTitle={i18n('patients.email', 'crs')}
          shouldTruncateText={false}
          title={emailCandidate?.email}
          Icon={<ContactMailIcon fontSize="small" />}
          endAdornment={
            <IcomxInlineTelecomAction
              candidates={candidates}
              resourceType={emailResource?.resourceType}
              resourceId={emailResource?.resourceId}
              system="email"
              tooltip="Open care team member email in iComx"
              value={emailCandidate?.email}
            />
          }
        />
      </Box>
    );
  };

  const MemberActionMenu = ({ member }: { member: CareTeamMemberGridRowData }) => {
    if (!isAllowedToEdit) {
      return null;
    }

    return (
      <ActionButton
        renderChildren={(_, setIsMenuOpen) => (
          <>
            <MenuItem
              onClick={() => {
                updateState({
                  isEditModalOpen: true,
                  memberToEdit: member,
                });
                setIsMenuOpen(false);
              }}
            >
              <EditIcon sx={{ marginRight: '10px' }} htmlColor="#00ab55" />
              <Typography variant={'body2'}>Edit</Typography>
            </MenuItem>
          </>
        )}
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      renderCell: (params) => {
        const member = params.row as CareTeamMemberGridRowData;
        return <CareTeamMemberCell member={member} />;
      },
    },
    {
      field: 'identifier',
      headerName: 'Identifier',
      flex: 1,
    },
    { field: 'memberType', headerName: 'Member Type', flex: 1 },
    { field: 'associatedOrg', headerName: 'Managing Organization', flex: 1 },
    { field: 'role', headerName: 'Role', flex: 1 },
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 1,
      sortComparator: (_, __, cellParams1, cellParams2) => {
        const { date: date1 } = cellParams1?.value ?? {};
        const { date: date2 } = cellParams2?.value ?? {};
        return momentDateComparator(date1, date2);
      },
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      flex: 1,
      sortComparator: (_, __, cellParams1, cellParams2) => {
        const { date: date1 } = cellParams1?.value ?? {};
        const { date: date2 } = cellParams2?.value ?? {};

        return momentDateComparator(date1, date2);
      },
    },
    {
      field: 'edit',
      headerName: '',
      flex: 0.3,
      sortable: false,
      renderCell: (params) => {
        const member = params.row as CareTeamMemberGridRowData;
        return <MemberActionMenu member={member} />;
      },
    },
  ];

  const handleClosePractitionerDrawer = () => {
    updateState({ isPractitionerDrawerOpen: false, memberToEdit: null });
  };

  return (
    <>
      <DataGridWithFilters
        addButtonTitle={'Manage Members'}
        title={i18n('case.details.careteams.addNewMember', 'crs')}
        onAddButtonClick={() => {
          updateState({
            isModalOpen: true,
          });
        }}
        onFilterDrawerOpen={onFilterDrawerOpen}
        rows={careTeamMembers}
        columns={columns}
        getRowId={(row) => row.id}
        getRowHeight={() => 'auto'}
        showPagination={showPagination}
        autoHeight={true}
        sx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'flex-start',
            py: 1,
          },
        }}
        onSearchTextFieldChange={onSearchTextFieldChange}
        searchTextFieldValue={searchTextFieldValue}
        isNestedGrid={isNestedGrid}
        showFilters={showFilters}
        Filters={
          <>
            <Box py={3}>
              <Box marginBottom={1}>
                <PeriodFilter
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  onStartDateChange={(newValue) => {
                    updateFilters({
                      startDate: newValue,
                    });
                  }}
                  onEndDateChange={(newValue) => {
                    updateFilters({
                      endDate: newValue,
                    });
                  }}
                />

                <Typography mb={1} mt={3} fontWeight={'bold'}>
                  Member Type
                </Typography>
                <Autocomplete
                  multiple
                  value={filters.memberTypes}
                  fullWidth
                  onChange={(_, newValue) => {
                    updateFilters({
                      memberTypes: newValue,
                    });
                  }}
                  options={careTeamMemberTypes}
                  getOptionLabel={({ label }: CareTeamMemberOption) => label}
                  renderInput={(params) => <TextField {...params} variant="outlined" />}
                />

                <Typography mb={1} mt={3} fontWeight={'bold'}>
                  Associated Organization
                </Typography>
                <Autocomplete
                  multiple
                  value={filters.associatedOrg}
                  fullWidth
                  onChange={(_, newValue) => {
                    updateFilters({
                      associatedOrg: newValue,
                    });
                  }}
                  options={associatedOrgs}
                  getOptionLabel={({ display }: Reference) => display ?? ''}
                  renderInput={(params) => <TextField {...params} variant="outlined" />}
                />

                <Typography mb={1} mt={3} fontWeight={'bold'}>
                  Role
                </Typography>
                <Autocomplete
                  multiple
                  value={filters.role}
                  fullWidth
                  onChange={(_, newValue) => {
                    updateFilters({
                      role: newValue,
                    });
                  }}
                  options={roles}
                  getOptionLabel={({ display }: Coding) => display ?? ''}
                  renderInput={(params) => <TextField {...params} variant="outlined" />}
                />
              </Box>
            </Box>
          </>
        }
        FilterDrawerProps={{
          title: i18n('case.details.careteams.filterPopUp.title', 'crs'),
          open: isFilterDrawerOpen,
          onApplyButtonClick: () => {
            onApplyFilters(filters);
          },
          onCloseIconButtonClick: onFilterDrawerClose,
          onClearAllButtonClick: onClearAllFilters,
        }}
      />
      <CareTeamMemberModal
        onMemberUpdateSuccess={onMemberUpdateSuccess}
        careTeamId={careTeam?.id as string}
        careTeamMembers={careTeamMembers}
        patient={patient}
        open={isModalOpen}
        onClose={() => {
          updateState({
            isModalOpen: false,
          });
        }}
      />
      <CareTeamMemberEditModal
        careTeamMember={memberToEdit}
        onMemberUpdateSuccess={onMemberUpdateSuccess}
        careTeam={careTeam}
        open={isEditModalOpen}
        onClose={() => {
          updateState({
            isEditModalOpen: false,
            memberToEdit: null,
          });
        }}
      />
      <PractitionerDrawer
        open={isPractitionerDrawerOpen}
        practitionerId={memberToEdit?.id}
        onClose={handleClosePractitionerDrawer}
        onCloseIconButtonClick={handleClosePractitionerDrawer}
      />
    </>
  );
};

const careTeamMemberTypes: CareTeamMemberOption[] = [
  { label: 'Practitioner', value: 'Practitioner' },
  { label: 'Practitioner Role', value: 'PractitionerRole' },
  { label: 'Care Team', value: 'CareTeam' },
];

export default CareTeamMembersGrid;
